import type { ApiClient } from './client.js';
import type { ClassroomDayDto, SearchEntryDto, DayReviewDto } from './types.js';

export interface UpdateDayBody {
  message?: string;
  questionDaily?: string;
  questionDailyAnswer?: string;
}

export interface RecordSearchBody {
  /** Classroom-local date the search happened on, YYYY-MM-DD. */
  date: string;
  kind: 'web' | 'youtube';
  engine: string;
  query: string;
}

/**
 * Day-history client: the per-day message + question (effective / carry-forward
 * on read; upsert today's row on write) and the search activity log. Date
 * params are ISO YYYY-MM-DD (classroom-local).
 */
export interface DayHistoryApi {
  getDay(classroomId: string, date: string): Promise<ClassroomDayDto>;
  putDay(classroomId: string, date: string, body: UpdateDayBody): Promise<ClassroomDayDto>;
  recordSearch(classroomId: string, body: RecordSearchBody): Promise<SearchEntryDto>;
  listSearches(classroomId: string, date: string): Promise<SearchEntryDto[]>;
  getReview(classroomId: string, date: string): Promise<DayReviewDto>;
  /** Dates with history (configured day or logged search), newest first. */
  listDays(classroomId: string, limit?: number): Promise<string[]>;
}

export function createDayHistoryApi(client: ApiClient): DayHistoryApi {
  return {
    async getDay(classroomId, date) {
      const res = await client.get<{ day: ClassroomDayDto }>(
        `/api/classrooms/${classroomId}/day/${date}`,
      );
      return res.day;
    },
    async putDay(classroomId, date, body) {
      const res = await client.put<{ day: ClassroomDayDto }>(
        `/api/classrooms/${classroomId}/day/${date}`,
        body,
      );
      return res.day;
    },
    async recordSearch(classroomId, body) {
      const res = await client.post<{ entry: SearchEntryDto }>(
        `/api/classrooms/${classroomId}/searches`,
        body,
      );
      return res.entry;
    },
    async listSearches(classroomId, date) {
      const res = await client.get<{ searches: SearchEntryDto[] }>(
        `/api/classrooms/${classroomId}/searches?date=${date}`,
      );
      return res.searches;
    },
    async getReview(classroomId, date) {
      const res = await client.get<{ review: DayReviewDto }>(
        `/api/classrooms/${classroomId}/day/${date}/review`,
      );
      return res.review;
    },
    async listDays(classroomId, limit) {
      const qs = limit ? `?limit=${limit}` : '';
      const res = await client.get<{ dates: string[] }>(`/api/classrooms/${classroomId}/days${qs}`);
      return res.dates;
    },
  };
}
